import { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';

/**
 * Live subscription to a team's integration-ID binding docs
 * (teams/{teamId}/memberIds — doc id = {templateTagId}---{xmid}).
 *
 * A subscription rather than a one-shot read, because this list is defined by
 * what is currently bound: it grows when a member scans a partner tag and shrinks
 * when an ID is released or a member is removed. A `get()` would be a screenshot,
 * and the unbind button would appear to do nothing until the page was reloaded.
 *
 * Pass enabled=false to skip the listener entirely (it is admin-only detail).
 *
 * @returns {Object[]} bindings ({id, templateTagId, xmid, tagId, memberClientId, ...})
 */
export function useTeamMemberIds(teamId, enabled = true) {
  const [bindings, setBindings] = useState([]);

  useEffect(() => {
    if (!teamId || !enabled) {
      setBindings([]);
      return undefined;
    }
    const unsubscribe = firebase.firestore()
      .collection('teams').doc(teamId)
      .collection('memberIds')
      .onSnapshot(
        (snapshot) => {
          setBindings(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        },
        (err) => {
          console.warn(`useTeamMemberIds: could not load memberIds for ${teamId}:`, err);
        },
      );
    return unsubscribe;
  }, [teamId, enabled]);

  return bindings;
}

/**
 * The bindings held by one member, newest first.
 *
 * Filtered from the team subscription rather than queried separately: a team has
 * few bindings, and the member details screen is reached from a team page that is
 * already listening. One listener beats one per member row.
 *
 * @returns {Object[]} bindings for memberClientId
 */
export function useMemberBindings(teamId, memberClientId, enabled = true) {
  const bindings = useTeamMemberIds(teamId, enabled && !!memberClientId);
  return bindings
    .filter((binding) => binding.memberClientId === memberClientId)
    .sort((a, b) => {
      // boundAt is a Firestore Timestamp; seconds is enough to order by and
      // avoids constructing Dates for every row on every render.
      const aSecs = (a.boundAt && a.boundAt.seconds) || 0;
      const bSecs = (b.boundAt && b.boundAt.seconds) || 0;
      return bSecs - aSecs;
    });
}
